<template>
  <div class="stats-page flex flex-col h-full bg-gray-50 dark:bg-[#0d1117] overflow-y-auto">
    <StatsFilterBar class="flex-shrink-0 sticky top-0 z-10" />

    <!-- Error ---------------------------------------------------------- -->
    <div v-if="store.error" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Try again</button>
    </div>

    <!-- Custom range only half chosen ---------------------------------- -->
    <div v-else-if="!store.isRangeComplete" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <p class="text-sm text-gray-400 dark:text-gray-500">Pick a start and end date to see your stats.</p>
    </div>

    <!-- Nothing matches ------------------------------------------------
         Deliberately distinct from "no tasks at all": one is an empty
         account and the other is an over-filtered view, and telling a new
         user their filters are wrong is unhelpful nonsense. -->
    <div v-else-if="showEmpty" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">
        {{ store.hasActiveFilters ? 'No tasks match these filters' : 'Nothing to report yet' }}
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm">
        {{ store.hasActiveFilters
          ? 'Try widening the date range or clearing a filter.'
          : 'Create and finish a few tasks and your progress will show up here.' }}
      </p>
      <button
        v-if="store.hasActiveFilters"
        @click="store.resetFilters()"
        class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >Clear filters</button>
    </div>

    <!-- Dashboard ------------------------------------------------------ -->
    <div v-else class="px-4 sm:px-5 py-4 sm:py-5 space-y-4">
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatKpiTile
          label="Completed" :metric="kpis.completed" :loading="loading"
          definition="Tasks you finished inside the selected period."
        />
        <StatKpiTile
          label="Completion rate" :metric="kpis.completion_rate" format="percent" :loading="loading"
          definition="Of tasks due in this period, the share you finished. Cancelled tasks count against it. Tasks with no due date are not counted at all."
        />
        <StatKpiTile
          label="On time" :metric="kpis.on_time_rate" format="percent" :loading="loading"
          definition="Of the tasks you finished that had a due date, the share finished on or before it."
        />
        <StatKpiTile
          label="Median turnaround" :metric="kpis.median_cycle_hours" format="duration" :loading="loading"
          :higher-is-better="false"
          definition="The middle time from creating a task to finishing it. Median rather than average, so one long forgotten task cannot distort it."
        />
        <StatKpiTile
          label="Overdue now" :metric="kpis.overdue_now" :loading="loading" as-of-now
          :higher-is-better="false"
          definition="Open tasks already past their due date, counted at this moment rather than across the period."
        />
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div class="xl:col-span-2">
          <CreatedVsCompleted :data="stats?.over_time || []" :bucket="stats?.range?.bucket" :loading="loading" />
        </div>
        <StatusDonut :data="stats?.by_status || []" :loading="loading" />
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <PriorityBars :data="stats?.by_priority || []" :loading="loading" />
        <CycleTimeHistogram
          :data="stats?.cycle_time || []"
          :median-hours="kpis.median_cycle_hours?.value"
          :loading="loading"
        />
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <OverdueAging :data="stats?.overdue_aging || []" :loading="loading" />
        <LongestOpenTable :rows="stats?.longest_open || []" :loading="loading" />
      </div>

      <MethodologyPanel />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useTaskStatsStore } from 'stores/taskStats.js'
import StatsFilterBar from './stats/StatsFilterBar.vue'
import StatKpiTile from './stats/StatKpiTile.vue'
import CreatedVsCompleted from './stats/CreatedVsCompleted.vue'
import StatusDonut from './stats/StatusDonut.vue'
import PriorityBars from './stats/PriorityBars.vue'
import CycleTimeHistogram from './stats/CycleTimeHistogram.vue'
import OverdueAging from './stats/OverdueAging.vue'
import LongestOpenTable from './stats/LongestOpenTable.vue'
import MethodologyPanel from './stats/MethodologyPanel.vue'

const store = useTaskStatsStore()

const stats = computed(() => store.stats)
const loading = computed(() => store.loading)

// Empty tiles rather than undefined while the first request is open, so the
// KPI row keeps its shape instead of collapsing and reflowing the page.
const EMPTY_KPIS = {
  completed: null, completion_rate: null, on_time_rate: null,
  median_cycle_hours: null, overdue_now: null,
}
const kpis = computed(() => stats.value?.kpis || EMPTY_KPIS)

// Only claim emptiness once something has actually loaded — otherwise the
// empty state flashes over the skeleton on every first paint.
const showEmpty = computed(() => !loading.value && stats.value && store.isEmpty)

async function retry() {
  store.invalidate()
  await store.fetchStats()
}

onMounted(() => store.fetchIfStale())
</script>
